/**
 * Arcℏimedian DSaaS — App Layout
 * Sidebar + Header + Content area for authenticated pages.
 */

import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../api/services';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';
import { theme as T } from '../theme';

// ── Inline SVG Icons ──
const Icon = ({ d, size = 20 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const Icons = {
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  database: 'M12 2C6.48 2 2 4.02 2 6.5v11C2 19.98 6.48 22 12 22s10-2.02 10-4.5v-11C22 4.02 17.52 2 12 2',
  layers: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-4a6 6 0 1 0 0-12 6 6 0 0 0 0 12zm0-4a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  gitBranch: 'M6 3v12M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 9a9 9 0 0 1-9 9',
  barChart: 'M12 20V10M18 20V4M6 20v-4',
  compare: 'M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M9 12h6',
  creditCard: 'M1 4h22v16H1zM1 10h22',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  logOut: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35',
  menu: 'M3 12h18M3 6h18M3 18h18',
  book: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6',
};

function VerificationBanner({ email }: { email: string }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    setLoading(true);
    try {
      await authService.resendVerification();
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: '#78350F', borderBottom: '1px solid #92400E',
      padding: '10px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexShrink: 0,
    }}>
      <span style={{ fontSize: 13, color: '#FEF3C7' }}>
        {t('nav.verifyBanner')} <strong>{email}</strong>.
      </span>
      {sent ? (
        <span style={{ fontSize: 12, color: '#6EE7B7', whiteSpace: 'nowrap' }}>
          {t('nav.linkSent')}
        </span>
      ) : (
        <button
          onClick={handleResend}
          disabled={loading}
          style={{
            fontSize: 12, fontWeight: 600, color: '#FEF3C7',
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
            whiteSpace: 'nowrap', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? t('nav.sendingLink') : t('nav.resend')}
        </button>
      )}
    </div>
  );
}

export function AppLayout() {
  const { t } = useTranslation();
  const { user, activeWorkspace, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const w = collapsed ? 72 : T.sidebarWidth;

  const NAV_ITEMS = [
    { to: '/', icon: Icons.home, label: t('nav.dashboard'), end: true },
    { to: '/datasets', icon: Icons.database, label: t('nav.datasets') },
    { to: '/models', icon: Icons.layers, label: t('nav.models') },
    { to: '/experiments', icon: Icons.target, label: t('nav.experiments') },
    { to: '/pipelines', icon: Icons.gitBranch, label: t('nav.pipelines') },
    { to: '/analyses', icon: Icons.barChart, label: t('nav.analyses') },
    { to: '/analyses/compare', icon: Icons.compare, label: t('nav.compare') },
    { to: '/billing', icon: Icons.creditCard, label: t('nav.billing') },
    { to: '/tutorial', icon: Icons.book, label: t('nav.tutorial') },
    { to: '/data-types', icon: Icons.grid, label: t('nav.dataTypes') },
    ...(user?.is_superuser ? [{ to: '/admin', icon: Icons.shield, label: t('nav.admin') }] : []),
    { to: '/settings', icon: Icons.settings, label: t('nav.settings') },
  ];

  const linkStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: collapsed ? '10px 0' : '10px 16px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: T.radiusSm,
    color: isActive ? T.accent : T.textMuted,
    background: isActive ? T.accentGlow : 'transparent',
    fontWeight: isActive ? 600 : 400,
    fontSize: 14,
    transition: 'all 0.15s',
    textDecoration: 'none',
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg }}>
      {/* ── Sidebar ── */}
      <aside style={{
        width: w,
        flexShrink: 0,
        borderRight: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        background: T.bgCard,
      }}>
        {/* Logo */}
        <NavLink
          to="/"
          end
          style={{
            height: T.headerHeight,
            display: 'flex',
            alignItems: 'center',
            padding: collapsed ? '0 16px' : '0 20px',
            borderBottom: `1px solid ${T.border}`,
            gap: 10,
            justifyContent: collapsed ? 'center' : 'flex-start',
            textDecoration: 'none',
          }}
        >
          <img
            src="/logo_archimedian.png"
            alt="Arcℏimedian"
            style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}
          />
          {!collapsed && (
            <span style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 600, color: T.text }}>
              Arc<span style={{ background: T.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>ℏ</span>imedian
            </span>
          )}
        </NavLink>

        {/* Workspace indicator */}
        {!collapsed && activeWorkspace && (
          <div style={{
            margin: '12px 12px 0',
            padding: '8px 12px',
            background: T.surface,
            borderRadius: T.radiusSm,
            fontSize: 12,
          }}>
            <div style={{ color: T.textDim, marginBottom: 2 }}>{t('nav.workspace')}</div>
            <div style={{ color: T.text, fontWeight: 500, fontSize: 13 }}>
              {activeWorkspace.emoji || '📊'} {activeWorkspace.name}
            </div>
          </div>
        )}

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={({ isActive }) => linkStyle(isActive)}
            >
              <Icon d={item.icon} size={20} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
          <a
            href="mailto:contact@archimedian.ai"
            style={linkStyle(false)}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.textMuted; (e.currentTarget as HTMLAnchorElement).style.background = T.surface; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.textMuted; (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
          >
            <Icon d={Icons.mail} size={20} />
            {!collapsed && <span>{t('nav.contact')}</span>}
          </a>
        </nav>

        {/* Language + Legal links */}
        {!collapsed && (
          <div style={{ padding: '8px 20px 4px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <LanguageSwitcher compact />
          </div>
        )}
        {!collapsed && (
          <div style={{ padding: '4px 20px 8px', display: 'flex', gap: 12 }}>
            <a href="/privacy" style={{ fontSize: 11, color: T.textDim, textDecoration: 'none' }}>{t('nav.privacy')}</a>
            <a href="/terms" style={{ fontSize: 11, color: T.textDim, textDecoration: 'none' }}>{t('nav.terms')}</a>
          </div>
        )}

        {/* User / Logout */}
        <div style={{
          padding: 12,
          borderTop: `1px solid ${T.border}`,
        }}>
          {!collapsed && user && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 8px 12px',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: T.surface,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 600, color: T.accent,
              }}>
                {user.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.full_name}
                </div>
                <div style={{ fontSize: 11, color: T.textDim }}>{user.role}</div>
              </div>
            </div>
          )}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: collapsed ? '10px 0' : '10px 16px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              width: '100%',
              background: 'none', border: 'none',
              color: T.textDim, fontSize: 13, cursor: 'pointer',
              borderRadius: T.radiusSm,
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.danger)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.textDim)}
          >
            <Icon d={Icons.logOut} size={18} />
            {!collapsed && <span>{t('nav.signOut')}</span>}
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <header style={{
          height: T.headerHeight,
          borderBottom: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          gap: 16,
          background: T.bgCard,
          flexShrink: 0,
        }}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: 'none', border: 'none', color: T.textMuted,
              cursor: 'pointer', padding: 4, display: 'flex',
            }}
          >
            <Icon d={Icons.menu} size={20} />
          </button>

          {/* Search */}
          <div style={{
            flex: 1, maxWidth: 480,
            display: 'flex', alignItems: 'center', gap: 8,
            background: T.surface, borderRadius: T.radiusSm,
            padding: '8px 14px',
          }}>
            <Icon d={Icons.search} size={16} />
            <input
              type="text"
              placeholder={t('nav.search')}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                color: T.text, fontSize: 13,
              }}
            />
            <kbd style={{
              padding: '2px 6px', borderRadius: 4,
              background: T.surfaceLight, color: T.textDim,
              fontSize: 11, fontFamily: T.fontMono,
            }}>⌘K</kbd>
          </div>

          <div style={{ flex: 1 }} />

          {/* Notifications */}
          <NotificationBell />
        </header>

        {/* Email verification banner */}
        {user && !user.email_verified && (
          <VerificationBanner email={user.email} />
        )}

        {/* Page Content */}
        <main style={{
          flex: 1, overflow: 'auto', padding: 24,
        }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
